import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const THEME_LABELS = {
  pink:   { name:'Rosa (predeterminado)', preview:'#e8547a' },
  purple: { name:'Morado', preview:'#7F77DD' },
  teal:   { name:'Verde azulado', preview:'#1D9E75' },
  dark:   { name:'Modo oscuro', preview:'#2d2d2d' },
}

const FONT_OPTIONS = [
  { id:'inter',    name:'Inter (predeterminado)',  style:'Inter, sans-serif' },
  { id:'georgia',  name:'Georgia (elegante)',      style:'Georgia, serif' },
  { id:'mono',     name:'Monospace (técnico)',     style:'"Courier New", monospace' },
  { id:'rounded',  name:'Redondeado',              style:'"Trebuchet MS", sans-serif' },
]

export default function Settings({ theme, themes, onChange }) {
  const [rules, setRules] = useState([])
  const [clients, setClients] = useState([])
  const [bulkDates, setBulkDates] = useState({}) // { clientId: date }
  const [bulkSaving, setBulkSaving] = useState(false)
  const [bulkDone, setBulkDone] = useState(false)
  const [showSetup, setShowSetup] = useState(false)

  useEffect(() => {
    fetchRules()
    supabase.from('clients').select('id,name,nickname,frequency').eq('is_active',true).order('name').then(({data})=>setClients(data||[]))
  }, [])

  async function saveBulkDates() {
    setBulkSaving(true)
    const entries = Object.entries(bulkDates).filter(([,d])=>d)
    for (const [clientId, date] of entries) {
      // Check if entry already exists for this client+date
      const { data: existing } = await supabase.from('cleaning_history')
        .select('id').eq('client_id', clientId).eq('cleaned_date', date).maybeSingle()
      if (!existing) {
        await supabase.from('cleaning_history').insert([{
          client_id: clientId,
          cleaned_date: date,
          hours_actual: 2,
          staff_names: 'Setup inicial',
          team_note: 'Fecha inicial configurada manualmente'
        }])
      }
    }
    setBulkSaving(false)
    setBulkDone(true)
    setTimeout(()=>setBulkDone(false), 4000)
  }
  const [loading, setLoading] = useState(true)
  const [font, setFont] = useState(() => localStorage.getItem('crm_font') || 'inter')

  function changeFont(f) {
    setFont(f)
    localStorage.setItem('crm_font', f)
    document.body.style.fontFamily = FONT_OPTIONS.find(o=>o.id===f)?.style || 'Inter, sans-serif'
  }

  async function fetchRules() {
    setLoading(true)
    const { data } = await supabase.from('automation_rules').select('*').order('created_at')
    setRules(data || [])
    setLoading(false)
  }

  async function toggleRule(id, current) {
    await supabase.from('automation_rules').update({ is_active: !current }).eq('id', id)
    setRules(prev => prev.map(r => r.id === id ? {...r, is_active: !current} : r))
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.title}>⚙️ Configuración</div>
        <div style={s.sub}>Personaliza el sistema y las automatizaciones</div>
      </div>

      {/* BULK SETUP TOOL */}
      <div style={{...s.panel, marginBottom:12, border:'2px solid #e8547a'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
          <div style={{...s.panelTitle, color:'#e8547a', margin:0}}>🛠 Setup inicial — Última limpieza por cliente</div>
          <button style={{fontSize:12,padding:'4px 10px',border:'1px solid #e8547a',borderRadius:7,background:showSetup?'#e8547a':'#fff',color:showSetup?'#fff':'#e8547a',cursor:'pointer'}}
            onClick={()=>setShowSetup(p=>!p)}>
            {showSetup?'Cerrar':'Abrir herramienta'}
          </button>
        </div>
        <div style={{fontSize:11,color:'#80868b'}}>Usa esto UNA SOLA VEZ para configurar las fechas iniciales. Después usa Completar Día normalmente.</div>
        {showSetup && (
          <div style={{marginTop:14}}>
            {bulkDone && <div style={{padding:'8px 12px',background:'#e1f5ee',borderRadius:8,color:'#085041',fontSize:12,fontWeight:500,marginBottom:10}}>✓ Fechas guardadas exitosamente</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:8,marginBottom:12}}>
              {clients.map(c=>(
                <div key={c.id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',background:'#f8f9fa',borderRadius:8,border:'1px solid #e3e5e8'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:500,color:'#202124',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.nickname||c.name}</div>
                    <div style={{fontSize:10,color:'#9aa0a6'}}>{c.frequency||'sin frecuencia'}</div>
                  </div>
                  <input type="date" style={{fontSize:11,padding:'4px 6px',border:`1px solid ${bulkDates[c.id]?'#e8547a':'#e3e5e8'}`,borderRadius:6,background:'#fff',color:'#202124',width:130}}
                    value={bulkDates[c.id]||''}
                    onChange={e=>setBulkDates(p=>({...p,[c.id]:e.target.value}))}/>
                </div>
              ))}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <button style={{padding:'9px 20px',background:'#e8547a',color:'#fff',border:'none',borderRadius:9,fontWeight:600,cursor:'pointer',fontSize:13}}
                onClick={saveBulkDates} disabled={bulkSaving||Object.values(bulkDates).filter(Boolean).length===0}>
                {bulkSaving?'Guardando...':'✓ Guardar '+Object.values(bulkDates).filter(Boolean).length+' fechas'}
              </button>
              <span style={{fontSize:11,color:'#9aa0a6'}}>{Object.values(bulkDates).filter(Boolean).length} de {clients.length} clientes con fecha</span>
            </div>
          </div>
        )}
      </div>

      <div style={s.grid}>
        {/* Theme */}
        <div style={s.panel}>
          <div style={s.panelTitle}>🎨 Tema de color</div>
          <div style={s.themeGrid}>
            {Object.entries(THEME_LABELS).map(([key, val]) => (
              <div key={key} style={{...s.themeCard, ...(theme===key?s.themeActive:{})}}
                onClick={() => onChange(key)}>
                <div style={{...s.themePreview, background: val.preview}}/>
                <div style={s.themeName}>{val.name}</div>
                {theme===key && <div style={s.themeCheck}>✓</div>}
              </div>
            ))}
          </div>
          <div style={{marginTop:16}}>
            <div style={{fontSize:12,fontWeight:500,color:'#5f6368',marginBottom:8}}>Fuente</div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {FONT_OPTIONS.map(f=>(
                <div key={f.id} style={{...s.themeCard,...(font===f.id?s.themeActive:{})}}
                  onClick={()=>changeFont(f.id)}>
                  <span style={{fontFamily:f.style,fontSize:14}}>Aa</span>
                  <div style={{...s.themeName,fontFamily:f.style}}>{f.name}</div>
                  {font===f.id&&<div style={s.themeCheck}>✓</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Automation rules */}
        <div style={s.panel}>
          <div style={s.panelTitle}>⚡ Reglas de automatización</div>
          <div style={s.panelSub}>Activa o desactiva las automatizaciones del sistema</div>
          {loading ? <div style={s.loading}>Cargando...</div> :
            rules.map(r => (
              <div key={r.id} style={s.ruleRow}>
                <div style={s.ruleIcon}>{r.icon}</div>
                <div style={{flex:1}}>
                  <div style={s.ruleText}>{r.label}</div>
                  <div style={s.ruleSub}>{r.description}</div>
                </div>
                <div style={{...s.toggle, background: r.is_active ? '#1d9e75' : '#ddd'}}
                  onClick={() => toggleRule(r.id, r.is_active)}>
                  <div style={{...s.toggleDot, left: r.is_active ? 14 : 2}}/>
                </div>
              </div>
            ))
          }
        </div>

        {/* Account */}
        <div style={s.panel}>
          <div style={s.panelTitle}>🔐 Cuenta</div>
          <div style={s.infoRow}><span style={s.infoLabel}>URL del sistema</span><span style={s.infoVal}>kittyscleaning.netlify.app</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Base de datos</span><span style={s.infoVal}>Supabase (segura)</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Versión</span><span style={s.infoVal}>1.0.0</span></div>
        </div>

        {/* Checklist Editor moved to Actividad → ✅ Checklists tab */}

        {/* Email Settings */}
        <div style={s.panel}>
          <div style={s.panelTitle}>📧 Configuración de email</div>
          <div style={{fontSize:11,color:'#9aa0a6',marginBottom:10,lineHeight:1.7}}>
            Usa <a href="https://emailjs.com" target="_blank" rel="noreferrer" style={{color:'#e8547a'}}>EmailJS.com</a> (gratis, 200 emails/mes).<br/>
            1. Crea cuenta → conecta tu Gmail → crea una plantilla<br/>
            2. En la plantilla usa estas variables: <code>{'{{to_email}}'}</code>, <code>{'{{to_name}}'}</code>, <code>{'{{invoice_num}}'}</code>, <code>{'{{amount}}'}</code>, <code>{'{{date}}'}</code>, <code>{'{{zelle}}'}</code>, <code>{'{{company_name}}'}</code><br/>
            3. Copia tus IDs abajo
          </div>
          {[['publicKey','Public Key'],['serviceId','Service ID'],['templateId','Template ID']].map(([k,l])=>{
            const keys = JSON.parse(localStorage.getItem('emailjs_keys')||'{}')
            return (
              <div key={k} style={s.mfield}>
                <label style={s.mlabel}>{l}</label>
                <input style={{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,background:'#f8f9fa'}}
                  defaultValue={keys[k]||''}
                  placeholder={k==='publicKey'?'user_xxxxxx':k==='serviceId'?'service_xxxxxx':'template_xxxxxx'}
                  onBlur={e=>{
                    const cur = JSON.parse(localStorage.getItem('emailjs_keys')||'{}')
                    cur[k] = e.target.value
                    localStorage.setItem('emailjs_keys', JSON.stringify(cur))
                    if (window.emailjs && cur.publicKey) window.emailjs.init(cur.publicKey)
                  }}/>
              </div>
            )
          })}
          <div style={{fontSize:11,color:'#9aa0a6',marginTop:4}}>Los cambios se guardan automáticamente al salir de cada campo.</div>
        </div>

        {/* SMS Template Editor */}
        <div style={s.panel}>
          <div style={s.panelTitle}>📱 Plantillas de SMS</div>
          <div style={{fontSize:11,color:'#9aa0a6',marginBottom:10}}>Personaliza los mensajes. Usa {'{nombre}'} para el nombre del cliente, {'{hora}'} para la hora, {'{ventana}'} para el rango de 15 min.</div>
          {[
            { key:'sms_client_template', label:'Mensaje a clientes (English)', default:`Hi {nombre}! This is a reminder from Kitty's Cleaning Services 🐱. We will arrive on {dia} between {hora} - {ventana}. See you soon!` },
            { key:'sms_staff_template', label:'Mensaje al personal (Español)', default:`Buenos días {nombre}! 🐱 Tu ruta del {dia}:\n\n{paradas}\n\n🔗 Ver ruta completa:\n{link}` },
            { key:'sms_update_template', label:'Mensaje de actualización (Personal)', default:`Perdón {nombre}, hubo un cambio en tu ruta de {dia}. Este es el horario actualizado:\n\n{paradas}\n\n🔗 Ver ruta actualizada:\n{link}` },
          ].map(tmpl => {
            const saved = localStorage.getItem(tmpl.key) || tmpl.default
            return (
              <div key={tmpl.key} style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:600,color:'#5f6368',marginBottom:4}}>{tmpl.label}</div>
                <textarea rows={4} style={{width:'100%',padding:'8px 10px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:11,background:'#f8f9fa',resize:'vertical',fontFamily:'inherit'}}
                  defaultValue={saved}
                  onBlur={e=>localStorage.setItem(tmpl.key,e.target.value)}/>
                <button style={{fontSize:10,padding:'2px 8px',border:'1px solid #e3e5e8',borderRadius:5,background:'#fff',color:'#80868b',cursor:'pointer',marginTop:3}}
                  onClick={()=>{ localStorage.setItem(tmpl.key,tmpl.default); window.location.reload() }}>
                  Restaurar original
                </button>
              </div>
            )
          })}
        </div>

        <div style={s.panel}>
          <div style={s.panelTitle}>📋 Próximas funciones</div>
          {[
            'Hoja de tiempo semanal para nómina',
            'Envío automático de SMS a clientes',
            'Enlace de solo lectura para el equipo',
            'Mapa de rutas con Google Maps',
            'Seguimiento GPS del equipo',
            'Integración con QuickBooks',
          ].map((item, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 0',borderBottom:'1px solid #f8f9fa',fontSize:12,color:'#5f6368'}}>
              <span style={{fontSize:10,color:'#bdc1c6'}}>○</span>{item}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const s = {
  page:{ padding:'20px 24px', overflowY:'auto', height:'100%' },
  header:{ marginBottom:20 },
  title:{ fontSize:20, fontWeight:600, color:'#202124' },
  sub:{ fontSize:12, color:'#80868b', marginTop:3 },
  grid:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 },
  panel:{ background:'#fff', borderRadius:12, border:'1px solid #e3e5e8', padding:'16px 18px' },
  panelTitle:{ fontSize:14, fontWeight:600, color:'#202124', marginBottom:4 },
  panelSub:{ fontSize:11, color:'#9aa0a6', marginBottom:12 },
  loading:{ fontSize:12, color:'#9aa0a6', padding:'12px 0' },
  themeGrid:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:10 },
  themeCard:{ border:'1.5px solid #e3e5e8', borderRadius:10, padding:'10px 12px', cursor:'pointer', display:'flex', alignItems:'center', gap:8, position:'relative' },
  themeActive:{ borderColor:'#e8547a', background:'#fdeef3' },
  themePreview:{ width:20, height:20, borderRadius:'50%', flexShrink:0 },
  themeName:{ fontSize:12, color:'#333', flex:1 },
  themeCheck:{ fontSize:12, color:'#e8547a', fontWeight:600 },
  ruleRow:{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #f8f9fa' },
  ruleIcon:{ fontSize:16, flexShrink:0 },
  ruleText:{ fontSize:12, fontWeight:500, color:'#202124' },
  ruleSub:{ fontSize:10, color:'#9aa0a6', marginTop:2 },
  toggle:{ width:30, height:18, borderRadius:9, cursor:'pointer', position:'relative', flexShrink:0, transition:'background .2s' },
  toggleDot:{ position:'absolute', width:14, height:14, borderRadius:'50%', background:'#fff', top:2, transition:'left .2s' },
  infoRow:{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f8f9fa', fontSize:12 },
  infoLabel:{ color:'#80868b' },
  infoVal:{ color:'#202124', fontWeight:500 },
}
